// src/components/FormLists/formListsGovernanceUtils.js

/**
 * formListsGovernanceUtils
 * -----------------------------------------------------------------------------
 * Centralized Form Lists governance policy helpers.
 *
 * PURPOSE
 *  • Keep SmartList/org-managed behavior out of the shared Spreadsheet/ engine.
 *  • Keep FormListsLabPage, FormListsDependencyCell, and apply utilities aligned.
 *  • Make the policy explicit:
 *      - SmartList option values are centrally governed and read-only here.
 *      - Form-specific dependency rules may still reference SmartList options.
 *
 * IMPORTANT
 *  • This file does NOT read/write React context.
 *  • This file does NOT read/write browser storage.
 *  • This file does NOT import Firebase.
 *  • This file does NOT mutate inputs.
 *  • This file does NOT decide runtime option resolution.
 *
 * ENTERPRISE POLICY
 *  • Local form lists:
 *      Option / Hint cells are editable.
 *      Dependency cells remain available.
 *
 *  • Org-managed SmartLists:
 *      Option / Hint cells are read-only unless governance explicitly allows
 *      local option editing.
 *      Dependency cells remain available because dependencies are form-specific.
 *
 * WHY THIS EXISTS
 *  • The same SmartList option can be used in many forms, each with different
 *    form-specific dependency rules.
 *  • Therefore, SmartLists own option definitions, while forms own dependency
 *    relationships.
 * -----------------------------------------------------------------------------
 */

import {
  isOrgManagedDropdownInstance,
} from '../../utils/managedFieldInstances';

import {
  FORM_LISTS_EDITABLE_CELL_KEYS,
} from './formListsGridConfig';

import {
  normalizeString,
} from '../Spreadsheet/groupedSpreadsheetUtils';

export const FORM_LISTS_SMARTLIST_READONLY_REASON =
  'This SmartList is managed in SmartLists. You can use it in form-specific dependencies, but option values are read-only here.';

export const FORM_LISTS_DEPENDENCY_DISABLED_REASON =
  'Dependency editing is disabled for this field.';

const getManagedIdentitySnapshot = (group = {}) => {
  const fieldMetadata = group?.fieldMetadata || group?.field || {};

  const managedFieldId = normalizeString(
    group?.managedFieldId ||
      fieldMetadata?.managedFieldId,
    '',
  );

  const managedOptionSetId = normalizeString(
    group?.managedOptionSetId ||
      fieldMetadata?.managedOptionSetId ||
      fieldMetadata?.optionsSource?.optionsSetId,
    '',
  );

  const optionsSource =
    normalizeString(group?.optionsSource, '') ||
    normalizeString(fieldMetadata?.optionsSource, '') ||
    normalizeString(fieldMetadata?.optionsSource?.type, '');

  return {
    fieldMetadata,
    managedFieldId,
    managedOptionSetId,
    optionsSource,
    orgManaged:
      group?.isOrgManaged === true ||
      fieldMetadata?.orgManaged === true,
  };
};

export const isFormListsOrgManagedGroup = (group = {}) => {
  if (!group || typeof group !== 'object') {
    return false;
  }

  const {
    fieldMetadata,
    managedFieldId,
    managedOptionSetId,
    optionsSource,
    orgManaged,
  } = getManagedIdentitySnapshot(group);

  if (isOrgManagedDropdownInstance(fieldMetadata)) {
    return true;
  }

  if (
    orgManaged &&
    managedFieldId &&
    managedOptionSetId
  ) {
    return true;
  }

  if (
    managedFieldId &&
    managedOptionSetId &&
    (
      optionsSource === 'organization' ||
      optionsSource === 'managedOptionSet'
    )
  ) {
    return true;
  }

  return false;
};

export const isFormListsManagedOptionValuesReadOnly = (group = {}) => {
  if (!isFormListsOrgManagedGroup(group)) {
    return false;
  }

  return group?.allowLocalOptionEditing !== true;
};

export const isFormListsEditableValueColumnKey = (columnKey) => {
  const safeColumnKey = normalizeString(columnKey, '');

  return FORM_LISTS_EDITABLE_CELL_KEYS.includes(safeColumnKey);
};

export const isFormListsDependencyColumnKey = (columnKey) => (
  normalizeString(columnKey, '') === 'dependency'
);

export const getFormListsColumnKey = ({
  column,
  columnKey,
} = {}) => (
  normalizeString(
    columnKey ||
      column?.key ||
      column?.id,
    '',
  )
);

/**
 * isFormListsValueCellReadOnly
 * -----------------------------------------------------------------------------
 * Returns whether a Form Lists spreadsheet value cell should be read-only.
 *
 * IMPORTANT
 *  • Only value-editable columns are governed here:
 *      - label / Option
 *      - hint / Hint
 *  • The dependency column is intentionally NOT blocked by this helper.
 *    Dependency cells are custom action cells and remain form-specific.
 */
export const isFormListsValueCellReadOnly = ({
  group,
  column,
  columnKey,
} = {}) => {
  const safeColumnKey = getFormListsColumnKey({
    column,
    columnKey,
  });

  if (!isFormListsEditableValueColumnKey(safeColumnKey)) {
    return false;
  }

  return isFormListsManagedOptionValuesReadOnly(group);
};

export const canApplyFormListOptionValues = ({
  group,
  includeReadOnlyGroups = false,
} = {}) => {
  const optionsSetUFI = normalizeString(group?.optionsSetUFI, '');

  if (!optionsSetUFI) {
    return false;
  }

  if (
    isFormListsManagedOptionValuesReadOnly(group) &&
    includeReadOnlyGroups !== true
  ) {
    return false;
  }

  return true;
};

/**
 * canConfigureFormListDependenciesForGroup
 * -----------------------------------------------------------------------------
 * Determines whether the group policy allows dependency configuration.
 *
 * IMPORTANT
 *  • This does NOT check row-level requirements like optionId.
 *  • This does NOT check field type restrictions like single-select trigger.
 *  • FormListsDependencyCell owns those UI-specific checks.
 */
export const canConfigureFormListDependenciesForGroup = (group = {}) => {
  if (!group || typeof group !== 'object') {
    return false;
  }

  return group?.allowLocalDependencies !== false;
};

export const getFormListsDependencyDisabledReason = (group = {}) => {
  if (group?.allowLocalDependencies === false) {
    return FORM_LISTS_DEPENDENCY_DISABLED_REASON;
  }

  return '';
};